import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout, 
  Menu, 
  Button, 
  Avatar, 
  Dropdown, 
  Typography, 
  Card, 
  Row, 
  Col, 
  Space,
  Badge,
  Statistic,
  Modal,
  Spin,
  message,
  Tag
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CameraOutlined,
  EnvironmentOutlined,
  DashboardOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import staffAuthManager from '../firebase/staffAuthService';
import attendanceManager from '../firebase/attendanceService';
import AttendanceCapture from '../Components/Staff/AttendanceCapture';
import StaffScheduleView from '../Components/Staff/StaffScheduleView';
import StaffAttendanceHistory from '../Components/Staff/StaffAttendanceHistory';
import StaffProfile from '../Components/Staff/StaffProfile';
import logoSmall from '../Images/logo_img_small.png';
import './StaffPortal.css';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;

const StaffPortal = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [selectedKey, setSelectedKey] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [attendanceType, setAttendanceType] = useState(null);
  const navigate = useNavigate();

  // Function to get time-based greeting
  const getTimeBasedGreeting = () => {
    const now = new Date();
    const hour = now.getHours();
    
    if (hour >= 5 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon';
    } else {
      return 'Good Evening';
    }
  };

  useEffect(() => {
    // Get current employee data
    const currentEmployee = staffAuthManager.getCurrentEmployee();
    setEmployee(currentEmployee);

    // Load attendance status
    loadAttendanceStatus();

    // Handle window resize
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    // Set initial state based on screen size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Force re-render of menu when attendance status changes
  useEffect(() => {
    // This will trigger a re-render when attendanceStatus or loadingAttendance changes
  }, [attendanceStatus, loadingAttendance]);

  const loadAttendanceStatus = async () => {
    setLoadingAttendance(true);
    try {
      const result = await attendanceManager.getTodayAttendanceStatus();
      if (result.success) {
        setAttendanceStatus(result);
      } else {
        console.error('Failed to load attendance status:', result.error);
        message.error('Failed to load attendance status');
      }
    } catch (error) {
      console.error('Error loading attendance status:', error);
      message.error('Error loading attendance status');
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleLogout = async () => {
    const result = await staffAuthManager.employeeLogout();
    if (result.success) {
      navigate('/staff-login');
    }
  };

  const handleMenuClick = (key) => {
    setSelectedKey(key);
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const handleBackToDashboard = () => {
    setSelectedKey('dashboard');
  };

  const handleAttendanceCapture = async (attendanceData) => {
    console.log('Attendance captured:', attendanceData);
    
    // Navigate back to dashboard immediately
    setSelectedKey('dashboard');
    
    // Show success message on dashboard
    const attendanceTypeText = attendanceData.attendanceType === 'clock-in' ? 'Clock In' : 'Clock Out';
    
    // Refresh attendance status to get updated state
    await loadAttendanceStatus();
  };

  const handleAttendanceNavigation = (type) => {
    console.log(`Navigating to attendance capture for ${type}`);
    
    // Set attendance type first
    setAttendanceType(type);
    
    // Navigate to attendance capture
    setSelectedKey('attendance');
    
    // On mobile, collapse the sidebar
    if (isMobile) {
      setCollapsed(true);
    }
    
    // Log navigation for debugging
    console.log('Attendance navigation completed', { type, selectedKey: 'attendance' });
  };

  const handleUserMenuClick = ({ key }) => {
    if (key === 'profile') {
      setSelectedKey('profile');
      if (isMobile) {
        setCollapsed(true);
      }
    }
  };

  const userMenuItems = [
    {
      key: 'email',
      label: employee?.displayName || employee?.email,
      disabled: true,
      style: { fontWeight: 'bold', color: '#a20' }
    },
    {
      type: 'divider',
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  // Dynamic menu items based on attendance status
  const getAttendanceLabel = () => {
    if (loadingAttendance) return 'Loading...';
    const canClockIn = !attendanceStatus?.status || 
                      attendanceStatus?.status === 'not-clocked-in' || 
                      attendanceStatus?.status === null;
    return canClockIn ? 'Clock In' : 'Clock Out';
  };

  const menuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
    },
    {
      key: 'attendance',
      label: getAttendanceLabel(),
    },
    {
      key: 'schedule',
      label: 'Schedule',
    },
    {
      key: 'history',
      label: 'Attendance',
    },
  ];

  const renderContent = () => {
    switch (selectedKey) {
      case 'dashboard':
        // Button state logic
        const canClockIn = !attendanceStatus?.status || 
                          attendanceStatus?.status === 'not-clocked-in' || 
                          attendanceStatus?.status === null;
        const canClockOut = attendanceStatus?.status === 'clocked-in';
        
        // Get clock out time if available
        const clockOutTime = attendanceStatus?.clockOutTime ? 
          attendanceStatus.clockOutTime.toLocaleTimeString('en-SG', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }) : null;

        return (
          <div style={{ padding: '24px' }}>
            {/* Employee Info Header */}
            <div className="staff-employee-info" style={{ 
              padding: '20px', 
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '20px', display: 'block', color: '#1890ff' }}>
                  {getTimeBasedGreeting()}, {employee?.displayName || 'Employee'}!
                </Text>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontSize: '16px', display: 'block' }}>
                  {new Date().toLocaleDateString('en-SG', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Text>
              </div>
              {/* Professional Status Tags */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '12px', 
                flexWrap: 'wrap',
                marginBottom: '8px'
              }}>
                {/* Main Status Tag */}
                {(() => {
                  const canClockIn = !attendanceStatus?.status || 
                                    attendanceStatus?.status === 'not-clocked-in' || 
                                    attendanceStatus?.status === null;
                  const canClockOut = attendanceStatus?.status === 'clocked-in';
                  
                  if (loadingAttendance) {
                    return (
                      <Tag color="default" icon={<ClockCircleOutlined />} style={{ 
                        padding: '6px 16px', 
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        Loading Status...
                      </Tag>
                    );
                  }
                  
                  if (canClockOut) {
                    return (
                      <Tag color="success" icon={<CheckCircleOutlined />} style={{ 
                        padding: '6px 16px', 
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        Clocked In
                      </Tag>
                    );
                  }
                  
                  if (attendanceStatus?.status === 'clocked-out') {
                    return (
                      <Tag color="processing" icon={<CheckCircleOutlined />} style={{ 
                        padding: '6px 16px', 
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        Clocked Out
                      </Tag>
                    );
                  }
                  
                  return (
                    <Tag color="default" icon={<ClockCircleOutlined />} style={{ 
                      padding: '6px 16px', 
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      Not Clocked In
                    </Tag>
                  );
                })()}
                
                {/* Clock In Time Tag */}
                {attendanceStatus?.clockInTime && (
                  <Tag color="blue" icon={<ClockCircleOutlined />} style={{ 
                    padding: '6px 16px', 
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    In: {attendanceStatus.clockInTime.toLocaleTimeString('en-SG', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Tag>
                )}
                
                {/* Clock Out Time Tag */}
                {clockOutTime && (
                  <Tag color="purple" icon={<ClockCircleOutlined />} style={{ 
                    padding: '6px 16px', 
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Out: {clockOutTime}
                  </Tag>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="staff-dashboard-buttons" style={{ maxWidth: '400px', margin: '0 auto' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button 
                  type={canClockIn ? "primary" : "default"}
                  size="large" 
                  icon={<ClockCircleOutlined />}
                  block
                  disabled={!canClockIn}
                  onClick={() => {
                    setAttendanceType('clock-in');
                    handleMenuClick('attendance');
                  }}
                  style={{ 
                    height: '60px', 
                    fontSize: '16px',
                    opacity: canClockIn ? 1 : 0.6
                  }}
                >
                  Clock In
                </Button>
                <Button 
                  type={canClockOut ? "primary" : "default"}
                  size="large" 
                  icon={<ClockCircleOutlined />}
                  block
                  disabled={!canClockOut}
                  onClick={() => {
                    setAttendanceType('clock-out');
                    handleMenuClick('attendance');
                  }}
                  style={{ 
                    height: '60px', 
                    fontSize: '16px',
                    opacity: canClockOut ? 1 : 0.6
                  }}
                >
                  Clock Out
                </Button>
                <Button 
                  size="large" 
                  block
                  onClick={() => handleMenuClick('schedule')}
                  className="staff-schedule-history-button"
                  style={{ height: '45px', fontSize: '16px' }}
                >
                  <div className="staff-button-content">
                    <div className="staff-button-left">
                      <CalendarOutlined className="staff-button-icon" />
                      <span className="staff-button-text">Job Schedule</span>
                    </div>
                    <span className="staff-button-arrow">›</span>
                  </div>
                </Button>
                <Button 
                  size="large" 
                  block
                  onClick={() => handleMenuClick('history')}
                  className="staff-schedule-history-button"
                  style={{ height: '45px', fontSize: '16px' }}
                >
                  <div className="staff-button-content">
                    <div className="staff-button-left">
                      <CameraOutlined className="staff-button-icon" />
                      <span className="staff-button-text">Attendance History</span>
                    </div>
                    <span className="staff-button-arrow">›</span>
                  </div>
                </Button>
              </Space>
            </div>
          </div>
        );
      case 'attendance':
        return (
          <div>
            <AttendanceCapture 
              onCapture={handleAttendanceCapture}
              attendanceType={attendanceType || (attendanceStatus?.status === 'clocked-in' ? 'clock-out' : 'clock-in')}
            />
          </div>
        );
      case 'schedule':
        return <StaffScheduleView />;
      case 'history':
        return <StaffAttendanceHistory />;
      case 'profile':
        return <StaffProfile employee={employee} />;
      default:
        return null;
    }
  };

  if (!employee) {
    return <div>Loading...</div>;
  }

  return (
    <Layout className="staff-portal-layout" style={{ minHeight: '100vh' }}>
      <Sider 
        collapsed={collapsed} 
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 80}
        width={130}
        style={{
          background: '#fff',
          boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
        }}
        trigger={null}
      >
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          {!collapsed ? (
            <Title level={4} style={{ margin: 0, color: '#a20000' }}>
              Staff Portal
            </Title>
          ) : (
            <UserOutlined style={{ fontSize: '24px', color: '#a20000' }} />
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => handleMenuClick(key)}
          style={{ border: 'none' }}
        />
      </Sider>

      <Layout>
        <Header style={{ 
          background: '#fff', 
          padding: '0 24px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && selectedKey === 'dashboard' && (
              <img 
                src={logoSmall} 
                alt="menu" 
                onClick={() => setCollapsed(!collapsed)}
                style={{ 
                  width: '45px', 
                  height: '45px', 
                  marginRight: 16, 
                  cursor: 'pointer',
                  objectFit: 'contain'
                }} 
              />
            )}
            {isMobile && selectedKey !== 'dashboard' && (
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToDashboard}
                style={{ marginRight: 16 }}
              />
            )}
            <Title level={4} style={{ margin: 0 }}>
              {selectedKey === 'profile' ? 'Profile' : (menuItems.find(item => item.key === selectedKey)?.label || 'Dashboard')}
            </Title>
            {staffAuthManager.isBypassMode() && (
              <div style={{ 
                marginLeft: 16, 
                padding: '4px 8px', 
                background: '#fff2e8', 
                border: '1px solid #ffb366',
                borderRadius: '4px',
                fontSize: '11px',
                color: '#d46b08'
              }}>
                🔧 BYPASS MODE
              </div>
            )}
          </div>

          <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
            <Avatar icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
          </Dropdown>
        </Header>

        <Content style={{ 
          margin: '0', 
          padding: '0',
          background: '#fff',
          borderRadius: '0',
          minHeight: 'calc(100vh - 112px)'
        }}>
          {renderContent()}
        </Content>
      </Layout>
    </Layout>
  );
};

export default StaffPortal;
